import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useSessionData } from '../../../contexts/SessionDataContext';
import { DEFAULT_ACCOUNT_CATEGORIES } from '../../../constants/accountCategories';

/**
 * Custom hook to fetch and cache account settings (available models, preferred model, categories)
 * Settings are cached to avoid repeated API calls
 * Only fetches settings for authenticated users
 */
const useAccountSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { getAccountSettings, accountSettings: cachedAccountSettings } = useSessionData();

  const defaultSettings = {
    availableModels: ['gpt-6-sol-high', 'gpt-6-sol', 'gpt-6-luna'],
    preferredModel: 'gpt-6-sol',
    categories: [...DEFAULT_ACCOUNT_CATEGORIES],
  };

  useEffect(() => {
    // Don't fetch settings if user is not authenticated or auth is still loading
    if (!isAuthenticated || authLoading) {
      // Set defaults for unauthenticated users
      setSettings(defaultSettings);
      setLoading(false);
      return;
    }

    const fetchSettings = async () => {
      try {
        setLoading(true);
        setError(null);
        const next = await getAccountSettings({ force: false });
        setSettings(next);
      } catch (err) {
        // Only log error if it's not a 401 (unauthorized) - that's expected for unauthenticated users
        if (err?.response?.status !== 401) {
          console.error('Error fetching account settings:', err);
        }
        setError(err);
        // Set defaults on error
        setSettings(defaultSettings);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [isAuthenticated, authLoading, getAccountSettings, cachedAccountSettings.lastFetchedAt]);

  return { settings, loading, error };
};

export default useAccountSettings;
